import { PlusOutlined } from '@ant-design/icons'
import { Button, Divider, Flex } from 'antd'
import { useState } from 'react'

import { usePersonsServiceGetInstitutionPersonListEndpoint } from 'src/api/queries'
import { Gender, Permissions } from 'src/api/requests'
import { PageBreadcrumbs, SearchBar, Select, RangePicker } from 'src/components'
import { useTablePagination, useUser } from 'src/hooks'
import { disabledFutureDate, legalAgePickerValue } from 'src/libs'
import { useLocales } from 'src/locales'
import { resetPagination } from 'src/utils'

import { SearchPersonModal, PersonsTable } from '../components'

export const PersonsPage = () => {
  const { t } = useLocales()
  const pagination = useTablePagination()

  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState<string>()
  const [gender, setGender] = useState<string>()
  const [shouldIncludeAnonymous, setShouldIncludeAnonymous] = useState(false)
  const [dateTo, setDateTo] = useState('')
  const [dateFrom, setDateFrom] = useState('')

  const { hasPermission } = useUser()
  const queryParams = {
    pageNumber: Number(pagination.current),
    pageSize: Number(pagination.pageSize),
    ...(!!search && {
      searchTerm: search
    }),
    ...(!!gender && {
      gender: gender as Gender
    }),
    ...(!!dateFrom &&
      !!dateTo && {
        birthDateFrom: dateFrom,
        birthDateTo: dateTo
      }),
    includeAnonymous: shouldIncludeAnonymous
  }

  const { data, isLoading } =
    usePersonsServiceGetInstitutionPersonListEndpoint(queryParams)

  const onSearch = (val: string) => {
    setSearch(val)
    resetPagination(pagination)
  }

  const onGenderChange = (val: string) => {
    setGender(val)
    resetPagination(pagination)
  }

  const onShouldIncludeAnonymousChange = (val: string) => {
    setShouldIncludeAnonymous(JSON.parse(val))
    resetPagination(pagination)
  }

  const onToggle = () => setIsOpen((prev) => !prev)

  const genderOptions = [
    {
      value: 'male',
      label: t('form.male')
    },
    {
      value: 'female',
      label: t('form.female')
    },
    {
      value: 'unknown',
      label: t('form.unknown')
    }
  ]

  const anonymousOptions = [
    {
      value: 'true',
      label: t('form.include_anonymous')
    },
    {
      value: 'false',
      label: t('form.not_include_anonymous')
    }
  ]

  return (
    <PageBreadcrumbs
      title={t('menu.persons')}
      items={[
        {
          title: t('menu.persons')
        }
      ]}
    >
      <Flex justify="space-between" gap={16} wrap="wrap">
        <Flex gap={16} wrap="wrap">
          <SearchBar
            maxWidth={330}
            onSearch={onSearch}
            placeholder={t('persons.idnp_name_surname_sime_code')}
          />

          <Select
            options={genderOptions}
            onChange={onGenderChange}
            placeholder={t('form.gender')}
            allowClear
          />

          <Select
            options={anonymousOptions}
            value={String(shouldIncludeAnonymous)}
            onChange={onShouldIncludeAnonymousChange}
          />

          <RangePicker
            setDateFrom={setDateFrom}
            setDateTo={setDateTo}
            resetPagination={() => resetPagination(pagination)}
            disabledDate={disabledFutureDate}
            defaultPickerValue={legalAgePickerValue}
            placement="bottomRight"
            placeholder={[
              t('persons.from_date_of_birth'),
              t('persons.to_date_of_birth')
            ]}
          />
        </Flex>

        {hasPermission(Permissions.PERSONS_PERSONS_VIEW_INSTITUTION) && (
          <Button onClick={onToggle} icon={<PlusOutlined />} type="primary">
            {t('persons.search_new_person')}
          </Button>
        )}
      </Flex>

      <Divider />

      <PersonsTable
        showCaseStatus={false}
        dataSource={data?.data}
        loading={isLoading}
        pagination={{ ...pagination, total: data?.totalCount }}
      />

      {isOpen && <SearchPersonModal onCancel={onToggle} />}
    </PageBreadcrumbs>
  )
}
